import {
  Flex,
  Title,
  Paper,
  Button,
  Text,
  Stack,
  Box,
  Loader,
  Anchor,
} from "@mantine/core";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import BankIDLogo from "../../assets/bank-id-logo.svg";

import { useStartLogin, useGetLoginStatus } from "../../hooks/api/";
import QRCode from "react-qr-code";
import { useEffect, useState } from "react";
import { useUpdateCustomerPOAData } from "../../hooks/ui/customerPOADataStore";

export const Route = createFileRoute("/login/")({
  component: LoginPage,
  beforeLoad: async () => {
    throw redirect({
      to: "/signup",
    });
  },
});

function LoginPage() {
  const navigate = useNavigate();
  const [hasLoginSessionFailed, setHasLoginSessionFailed] = useState(false);
  const startLogin = useStartLogin();
  const getLoginStatus = useGetLoginStatus({
    id: startLogin.data?.body?.id,
    enabled: startLogin.isSuccess && !hasLoginSessionFailed,
  });
  const { updatePersonalNumber, updateTransactionId } =
    useUpdateCustomerPOAData();

  useEffect(() => {
    if (getLoginStatus.data?.body?.status === "failed") {
      setHasLoginSessionFailed(true);
    }
    if (getLoginStatus.data?.body?.status === "complete") {
      const personalNumber =
        getLoginStatus.data?.body?.providerInfo?.seBankID?.completionData?.user
          ?.personalNumber;
      const transactionId = getLoginStatus.data?.body?.id;
      updatePersonalNumber(personalNumber);
      updateTransactionId(transactionId);
      navigate({
        to: "/signin",
      });
    }
    return () => {
      setHasLoginSessionFailed(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getLoginStatus.data?.body]);

  return (
    <Flex justify={"center"} pt={"xl"}>
      {startLogin.isSuccess && !hasLoginSessionFailed ? (
        <>
          <Stack p={"lg"} maw={"770px"} w={"100%"}>
            <Title
              order={2}
              ff={"var(--wallet-font-family-subjecttivity)"}
              mb={"md"}
            >
              Var vänlig legitimera dig med BankID
            </Title>
            <Paper p={30} radius={"24px"} mb={"2rem"}>
              <Flex align="center" justify={"space-between"} mb={"xl"}>
                <Box>
                  <Title order={5} mb="md">
                    Gör såhär:
                  </Title>
                  <Text pr={"xl"}>
                    Starta BankID-appen på mobila enhet och tryck på “Skanna
                    QR-kod”. Rikta kameran mot QR-koden och signera.
                  </Text>
                </Box>
                <Box>
                  {getLoginStatus.isLoading && <Loader />}
                  {getLoginStatus.isSuccess && (
                    <QRCode
                      value={
                        getLoginStatus.data.body.providerInfo.seBankID.qrData
                      }
                    />
                  )}
                </Box>
              </Flex>
              <Text ta="center" mb={"xl"}>
                Har du inte Mobil BankID?{" "}
                <Anchor
                  href={`bankid:///?autostarttoken=${getLoginStatus.data?.body?.providerInfo?.seBankID?.autoStartToken}&redirect=null`}
                >
                  Använd BankID på samma enhet
                </Anchor>
              </Text>
              <Button
                variant="outline"
                fullWidth
                onClick={() => setHasLoginSessionFailed(true)}
              >
                Cancel
              </Button>
            </Paper>
          </Stack>
        </>
      ) : (
        <Stack p={"lg"} maw={"770px"} w={"100%"}>
          <Flex direction="column" w={"100%"}>
            {hasLoginSessionFailed ? (
              <>
                <Title
                  order={2}
                  mb="md"
                  ff={"var(--wallet-font-family-subjecttivity)"}
                >
                  Hoppsan! <br />
                  Något gick fel, vänligen försök igen.
                </Title>
                <Text>
                  Det gick inte att logga in. Det kan vara för att det tog för
                  långt tid eller för att signeringen avbröts. Försök igen eller
                  kontakta support om problemet kvarstår!
                </Text>
              </>
            ) : (
              <>
                <Title
                  order={2}
                  mb="md"
                  ff={"var(--wallet-font-family-subjecttivity)"}
                >
                  Välkommen
                </Title>
                <Text>
                  I dina sidor kan du se hela ditt pensionssparande, samt följ
                  flytten av dina försäkringar. Här finns även alla viktiga
                  dokument samlade på ett och samma ställe. Du loggar in enkelt
                  med BankID.
                </Text>
              </>
            )}
          </Flex>
          <Paper p={30} radius={"24px"} mb={"2rem"}>
            <Stack align="center">
              <Button
                fullWidth
                onClick={() => {
                  setHasLoginSessionFailed(false);
                  startLogin.mutateAsync();
                }}
                disabled={startLogin.isPending}
                loading={startLogin.isPending}
              >
                <Flex component="span" align="center">
                  <Box component="span" mr="0.5rem">
                    Logga in
                  </Box>
                  <BankIDLogo />
                </Flex>
              </Button>
              <Button variant="outline" fullWidth>
                Tillbaka till startsidan
              </Button>
            </Stack>
          </Paper>
        </Stack>
      )}
    </Flex>
  );
}
